import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import {
  Search,
  Plus,
  FileText,
  MapPin,
  Calendar,
  ChevronRight,
  Loader2,
  Filter,
  PoundSterling,
  TrendingUp,
  Clock,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  BarChart3,
  Phone,
  Mail,
  Building2,
  User,
  Info
} from 'lucide-react';
import { format, parseISO, startOfMonth, endOfMonth, subMonths, differenceInDays } from 'date-fns';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { ChevronDown } from 'lucide-react';
import { QuickStatusDialog } from '../components/tenders/QuickStatusDialog';

// 'Pipeline' shows Active + Submitted (default view for live tenders requiring attention)
const STATUS_OPTIONS = ['Pipeline', 'All', 'Active', 'Submitted', 'Won', 'Lost', 'Archived', 'Void'];
const PROJECT_TYPE_OPTIONS = ['All', 'Extension', 'Loft Conversion', 'Garage Conversion', 'Refurb', 'New Build', 'Other'];
const PLANNING_STATUS_OPTIONS = ['All', 'Approved', 'In Process', 'Exploring Options'];
const RETURN_DATE_FILTER_OPTIONS = [
  { value: 'all', label: 'All Return Dates' },
  { value: '7', label: 'Due within 7 days' },
  { value: '14', label: 'Due within 14 days' },
  { value: '30', label: 'Due within 30 days' },
  { value: 'overdue', label: 'Overdue' }
];
const SORT_OPTIONS = [
  { value: 'tender_number', label: 'Tender Number' },
  { value: 'return_date_asc', label: 'Return Date (Soonest)' },
  { value: 'return_date_desc', label: 'Return Date (Latest)' },
  { value: 'date_received', label: 'Date Received' }
];

const STATUS_COLORS = {
  'Active': 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  'Submitted': 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
  'Won': 'bg-green-500/20 text-green-400 border-green-500/30',
  'Lost': 'bg-red-500/20 text-red-400 border-red-500/30',
  'Archived': 'bg-gray-500/20 text-gray-400 border-gray-500/30',
  'Void': 'bg-gray-500/20 text-gray-400 border-gray-500/30'
};
const QUICK_STATUSES = ['Active', 'Submitted', 'Won', 'Lost', 'Archived', 'Void'];
const STATUS_DOT = { Active: 'bg-blue-400', Submitted: 'bg-cyan-400', Won: 'bg-green-400', Lost: 'bg-red-400', Archived: 'bg-gray-400', Void: 'bg-gray-500' };

const DASHBOARD_DATE_OPTIONS = [
  { value: 'all', label: 'All Time' },
  { value: 'this_month', label: 'This Month' },
  { value: 'last_month', label: 'Last Month' },
  { value: 'last_3_months', label: 'Last 3 Months' },
  { value: 'last_6_months', label: 'Last 6 Months' },
  { value: 'this_year', label: 'This Year' }
];

export function TendersList() {
  const [tenders, setTenders] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('Active');  // Default to Active — primary working view
  const [projectTypeFilter, setProjectTypeFilter] = useState('All');
  const [planningStatusFilter, setPlanningStatusFilter] = useState('All');
  const [dashboardDateFilter, setDashboardDateFilter] = useState('all');
  const [returnDateFilter, setReturnDateFilter] = useState('all');  // Return date filter
  const [sortBy, setSortBy] = useState('tender_number');  // Sort option
  const [quickStatus, setQuickStatus] = useState(null);  // { tender, nextStatus } for the dashboard status-change dialog
  
  const { api, isAdmin, canManageTenders, canEditRecords } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    // Allow both admins and surveyors to view tenders
    if (!canManageTenders) {
      navigate('/projects');
      return;
    }
    fetchTenders();
  }, [search, statusFilter, projectTypeFilter, planningStatusFilter, returnDateFilter, sortBy, canManageTenders]);

  useEffect(() => {
    if (canManageTenders) {
      fetchDashboard();
    }
  }, [dashboardDateFilter, canManageTenders]);

  const getDateRange = (filter) => {
    const now = new Date();
    switch (filter) {
      case 'this_month':
        return { start: format(startOfMonth(now), 'yyyy-MM-dd'), end: format(endOfMonth(now), 'yyyy-MM-dd') };
      case 'last_month':
        const lastMonth = subMonths(now, 1);
        return { start: format(startOfMonth(lastMonth), 'yyyy-MM-dd'), end: format(endOfMonth(lastMonth), 'yyyy-MM-dd') };
      case 'last_3_months':
        return { start: format(subMonths(now, 3), 'yyyy-MM-dd'), end: format(now, 'yyyy-MM-dd') };
      case 'last_6_months':
        return { start: format(subMonths(now, 6), 'yyyy-MM-dd'), end: format(now, 'yyyy-MM-dd') };
      case 'this_year':
        return { start: format(new Date(now.getFullYear(), 0, 1), 'yyyy-MM-dd'), end: format(now, 'yyyy-MM-dd') };
      default:
        return { start: null, end: null };
    }
  };

  const fetchTenders = async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      // Handle special filter values - 'Pipeline' and 'All' require client-side filtering
      if (statusFilter && !['Pipeline', 'All'].includes(statusFilter)) {
        params.append('status', statusFilter);
      }
      if (projectTypeFilter && projectTypeFilter !== 'All') params.append('project_type', projectTypeFilter);
      if (planningStatusFilter && planningStatusFilter !== 'All') params.append('planning_status', planningStatusFilter);
      
      // Add return date filter
      if (returnDateFilter && returnDateFilter !== 'all' && returnDateFilter !== 'overdue') {
        params.append('return_due_within_days', returnDateFilter);
      }
      
      // Add sort parameters
      if (sortBy === 'return_date_asc') {
        params.append('sort_by', 'return_date');
        params.append('sort_order', 'asc');
      } else if (sortBy === 'return_date_desc') {
        params.append('sort_by', 'return_date');
        params.append('sort_order', 'desc');
      } else if (sortBy === 'date_received') {
        params.append('sort_by', 'date_received');
        params.append('sort_order', 'desc');
      }
      
      const response = await api.get(`/tenders?${params.toString()}`);
      let data = response.data;
      
      // If "Pipeline" filter, show only Active and Submitted
      if (statusFilter === 'Pipeline') {
        data = data.filter(t => ['Active', 'Submitted'].includes(t.status));
      }
      // If "All" filter, show everything (no filtering needed)
      
      // Handle overdue filter (client-side)
      if (returnDateFilter === 'overdue') {
        const today = new Date().toISOString().split('T')[0];
        data = data.filter(t => t.return_date && t.return_date < today);
      }
      
      setTenders(data);
    } catch (error) {
      console.error('Failed to fetch tenders:', error);
      toast.error('Failed to load tenders');
    } finally {
      setLoading(false);
    }
  };

  const fetchDashboard = async () => {
    try {
      const { start, end } = getDateRange(dashboardDateFilter);
      let url = '/tenders/dashboard';
      if (start && end) {
        url += `?start_date=${start}&end_date=${end}`;
      }
      const response = await api.get(url);
      setDashboard(response.data);
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);
    }
  };

  const formatCurrency = (value) => {
    if (!value && value !== 0) return '£0';
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  // Get return date urgency status.
  // For submitted tenders the calculation is FROZEN at the submission date —
  // we never want a submitted tender's overdue counter to keep climbing day after day.
  const getReturnDateStatus = (tender) => {
    if (!tender.return_date) return null;

    // Anchor day: submitted_date if the tender has been submitted, else today.
    const anchor = tender.submitted_date ? new Date(tender.submitted_date) : new Date();
    anchor.setHours(0, 0, 0, 0);

    const returnDate = new Date(tender.return_date);
    returnDate.setHours(0, 0, 0, 0);
    const days = differenceInDays(returnDate, anchor);

    if (days < 0) return { status: 'overdue', days: Math.abs(days), label: `${Math.abs(days)}d overdue`, color: 'text-red-400 bg-red-500/20' };
    if (days === 0) return { status: 'today', days: 0, label: tender.submitted_date ? 'Submitted on day' : 'Due today', color: 'text-red-400 bg-red-500/20' };
    if (tender.submitted_date) return { status: 'on_time', days, label: `${days}d early`, color: 'text-green-400 bg-green-500/20' };
    if (days <= 7) return { status: 'urgent', days, label: `${days}d left`, color: 'text-amber-400 bg-amber-500/20' };
    if (days <= 14) return { status: 'soon', days, label: `${days}d left`, color: 'text-yellow-400 bg-yellow-500/20' };
    return { status: 'normal', days, label: `${days}d left`, color: 'text-gray-400 bg-gray-500/20' };
  };

  // Check if a tender is overdue (Active, >30 days since received, no submitted date)
  const isOverdue = (tender) => {
    if (tender.status !== 'Active' || tender.submitted_date) return false;
    const dateReceived = tender.date_received || tender.created_at?.slice(0, 10);
    if (!dateReceived) return false;
    try {
      const days = differenceInDays(new Date(), parseISO(dateReceived));
      return days > 30;
    } catch {
      return false;
    }
  };

  const clearFilters = () => {
    setSearch('');
    setStatusFilter('Active');  // Reset to Active (default)
    setProjectTypeFilter('All');
    setPlanningStatusFilter('All');
    setReturnDateFilter('all');
    setSortBy('tender_number');
  };

  const hasActiveFilters = search || statusFilter !== 'Active' || projectTypeFilter !== 'All' || planningStatusFilter !== 'All' || returnDateFilter !== 'all' || sortBy !== 'tender_number';

  if (!canManageTenders) {
    return null;
  }

  return (
    <div className="container mx-auto px-4 py-6 md:py-8 max-w-7xl">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h1 
            className="text-3xl md:text-4xl font-bold text-white"
            style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
          >
            Tenders
          </h1>
          <p className="text-gray-400 mt-1">
            {tenders.length} tender{tenders.length !== 1 ? 's' : ''} found
          </p>
        </div>
        
        <Button
          onClick={() => navigate('/tenders/new')}
          data-testid="new-tender-btn"
          className="bg-[#F5A623] text-[#1E2530] hover:bg-[#D98E1D] font-semibold"
        >
          <Plus className="w-4 h-4 mr-2" />
          New Tender
        </Button>
      </div>

      {/* Dashboard Metrics */}
      {dashboard && (
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold text-white flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-[#F5A623]" />
              Dashboard
            </h2>
            <Select value={dashboardDateFilter} onValueChange={setDashboardDateFilter}>
              <SelectTrigger className="w-[160px] bg-[#1E2530] border-white/20 text-white text-sm" data-testid="dashboard-date-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#2A3240] border-white/10">
                {DASHBOARD_DATE_OPTIONS.map(opt => (
                  <SelectItem key={opt.value} value={opt.value} className="text-white focus:bg-[#F5A623]/20">
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {/* Pipeline Value (formerly Contract Value) */}
            <Card className="bg-[#2A3240] border-white/10">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-1.5">
                      <p className="text-xs text-gray-500 uppercase mb-1">Pipeline Value</p>
                      {/* Info icon - desktop only */}
                      <TooltipProvider delayDuration={200}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button 
                              className="hidden md:inline-flex text-gray-500 hover:text-gray-300 transition-colors focus:outline-none focus:ring-1 focus:ring-[#F5A623]/50 rounded"
                              onClick={(e) => e.stopPropagation()}
                              aria-label="Pipeline Value information"
                            >
                              <Info className="w-3.5 h-3.5" />
                            </button>
                          </TooltipTrigger>
                          <TooltipContent 
                            className="bg-[#1E2530] border-white/10 text-white max-w-[280px] text-sm"
                            side="bottom"
                          >
                            <p>Total value of all Active and Submitted tenders awaiting a decision. Sum of {dashboard.active_count + (dashboard.submitted_count || 0)} tenders ({dashboard.active_count} Active + {dashboard.submitted_count || 0} Submitted).</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                    <p className="text-xl md:text-2xl font-bold text-white" data-testid="total-value">
                      {formatCurrency(dashboard.total_tender_value)}
                    </p>
                  </div>
                  <div className="w-10 h-10 rounded-full bg-[#F5A623]/20 flex items-center justify-center">
                    <PoundSterling className="w-5 h-5 text-[#F5A623]" />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Won Value */}
            <Card className="bg-[#2A3240] border-white/10">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-1.5">
                      <p className="text-xs text-gray-500 uppercase mb-1">Won Value</p>
                      {/* Info icon - desktop only */}
                      <TooltipProvider delayDuration={200}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button 
                              className="hidden md:inline-flex text-gray-500 hover:text-gray-300 transition-colors focus:outline-none focus:ring-1 focus:ring-[#F5A623]/50 rounded"
                              onClick={(e) => e.stopPropagation()}
                              aria-label="Won Value information"
                            >
                              <Info className="w-3.5 h-3.5" />
                            </button>
                          </TooltipTrigger>
                          <TooltipContent 
                            className="bg-[#1E2530] border-white/10 text-white max-w-[280px] text-sm"
                            side="bottom"
                          >
                            <p>Total value of tenders marked as Won within the selected time period. These have converted to live projects.</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                    <p className="text-xl md:text-2xl font-bold text-green-400" data-testid="won-value">
                      {formatCurrency(dashboard.successful_tender_value)}
                    </p>
                  </div>
                  <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                    <CheckCircle2 className="w-5 h-5 text-green-400" />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Pending Count */}
            <Card className="bg-[#2A3240] border-white/10">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-1.5">
                      <p className="text-xs text-gray-500 uppercase mb-1">Pending</p>
                      {/* Info icon - desktop only */}
                      <TooltipProvider delayDuration={200}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button 
                              className="hidden md:inline-flex text-gray-500 hover:text-gray-300 transition-colors focus:outline-none focus:ring-1 focus:ring-[#F5A623]/50 rounded"
                              onClick={(e) => e.stopPropagation()}
                              aria-label="Pending information"
                            >
                              <Info className="w-3.5 h-3.5" />
                            </button>
                          </TooltipTrigger>
                          <TooltipContent 
                            className="bg-[#1E2530] border-white/10 text-white max-w-[280px] text-sm"
                            side="bottom"
                          >
                            <p>Count of Active tenders awaiting submission or decision. Overdue ({dashboard.overdue_count}) = past return date with no submission recorded.</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                    <p className="text-xl md:text-2xl font-bold text-amber-400" data-testid="pending-count">
                      {dashboard.pending_count}
                    </p>
                    <div className="flex items-center gap-2 mt-1">
                      <p className="text-xs text-gray-500">
                        {dashboard.active_count} active
                      </p>
                      {dashboard.overdue_count > 0 && (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger>
                              <span className="flex items-center gap-1 text-xs text-orange-400 bg-orange-500/20 px-1.5 py-0.5 rounded">
                                <AlertTriangle className="w-3 h-3" />
                                {dashboard.overdue_count} overdue
                              </span>
                            </TooltipTrigger>
                            <TooltipContent className="bg-[#1E2530] border-white/10 text-white">
                              <p>Active tenders with no submission date after 30+ days</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                    </div>
                  </div>
                  <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
                    <Clock className="w-5 h-5 text-amber-400" />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Conversion Rate */}
            <Card className="bg-[#2A3240] border-white/10">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-1.5">
                      <p className="text-xs text-gray-500 uppercase mb-1">Conversion</p>
                      {/* Info icon - desktop only */}
                      <TooltipProvider delayDuration={200}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button 
                              className="hidden md:inline-flex text-gray-500 hover:text-gray-300 transition-colors focus:outline-none focus:ring-1 focus:ring-[#F5A623]/50 rounded"
                              onClick={(e) => e.stopPropagation()}
                              aria-label="Conversion Rate information"
                            >
                              <Info className="w-3.5 h-3.5" />
                            </button>
                          </TooltipTrigger>
                          <TooltipContent 
                            className="bg-[#1E2530] border-white/10 text-white max-w-[280px] text-sm"
                            side="bottom"
                          >
                            <p>Win rate: Won ÷ (Won + Lost). Calculation: {dashboard.won_count} won ÷ ({dashboard.won_count} won + {dashboard.lost_count} lost) = {dashboard.conversion_rate}%. Active and Void tenders are excluded.</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                    <p className="text-xl md:text-2xl font-bold text-purple-400" data-testid="conversion-rate">
                      {dashboard.conversion_rate}%
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {dashboard.won_count} won / {dashboard.lost_count} lost
                    </p>
                  </div>
                  <div className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-purple-400" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Conversion by Project Type - Full Width */}
          <Card className="bg-[#2A3240] border-white/10 mt-3">
            <CardContent className="p-4">
              <p className="text-xs text-gray-500 uppercase mb-3">Conversion by Project Type</p>
              {dashboard.conversion_by_project_type?.length > 0 ? (
                <div className="flex flex-wrap justify-start gap-6 md:gap-8">
                  {dashboard.conversion_by_project_type.map((pt, idx) => {
                    const hasDecidedTenders = (pt.won_count + pt.lost_count) > 0;
                    const pipelineCount = pt.active_count || 0;
                    
                    return (
                      <div key={pt.project_type} className="flex items-start gap-6 md:gap-8">
                        <div className="min-w-[110px]">
                          <div className="flex items-center gap-1.5 mb-1">
                            <p className="text-sm text-gray-400 font-medium">{pt.project_type}</p>
                            {/* Info icon - desktop only */}
                            <TooltipProvider delayDuration={200}>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <button 
                                    className="hidden md:inline-flex text-gray-500 hover:text-gray-300 transition-colors focus:outline-none focus:ring-1 focus:ring-[#F5A623]/50 rounded"
                                    onClick={(e) => e.stopPropagation()}
                                    aria-label={`${pt.project_type} conversion information`}
                                  >
                                    <Info className="w-3 h-3" />
                                  </button>
                                </TooltipTrigger>
                                <TooltipContent 
                                  className="bg-[#1E2530] border-white/10 text-white max-w-[280px] text-sm"
                                  side="bottom"
                                >
                                  {hasDecidedTenders ? (
                                    <p>Conversion rate for {pt.project_type} tenders. {pt.won_count} won ÷ ({pt.won_count} won + {pt.lost_count} lost). Pipeline: {pt.pipeline_value > 0 ? formatCurrency(pt.pipeline_value) : '£0'}.</p>
                                  ) : (
                                    <p>No decided tenders in this category yet. {pipelineCount} currently in pipeline.</p>
                                  )}
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </div>
                          <p className="text-2xl font-bold text-purple-400">
                            {hasDecidedTenders ? `${pt.conversion_rate}%` : '—'}
                          </p>
                          <p className="text-sm text-gray-500 mt-1">
                            <span className="text-green-400">{pt.won_count}W</span>
                            <span className="mx-1">/</span>
                            <span className="text-red-400">{pt.lost_count}L</span>
                          </p>
                          
                          {/* Value metrics */}
                          <div className="mt-3 pt-3 border-t border-white/10 space-y-1.5">
                            <p className="text-sm">
                              <span className="text-gray-500">Won:</span>{' '}
                              <span className="text-green-400 font-medium">{pt.won_value > 0 ? formatCurrency(pt.won_value) : '—'}</span>
                            </p>
                            <p className="text-sm">
                              <span className="text-gray-500">Lost:</span>{' '}
                              <span className="text-red-400 font-medium">{pt.lost_value > 0 ? formatCurrency(pt.lost_value) : '—'}</span>
                            </p>
                            <p className="text-sm">
                              <span className="text-gray-500">Pipeline:</span>{' '}
                              <span className="text-amber-400 font-medium">{pt.pipeline_value > 0 ? formatCurrency(pt.pipeline_value) : '—'}</span>
                            </p>
                            {/* Share of Won Revenue - distinct style */}
                            {pt.won_value > 0 && (
                              <p className="text-sm mt-2 pt-2 border-t border-white/10">
                                <span className="text-cyan-400 font-semibold">{pt.share_of_won_revenue}%</span>
                                <span className="text-gray-500 text-xs ml-1">of won revenue</span>
                              </p>
                            )}
                          </div>
                        </div>
                        {idx < dashboard.conversion_by_project_type.length - 1 && (
                          <div className="hidden md:block h-40 w-px bg-white/10 self-center" />
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-sm text-gray-500">No conversion data yet</p>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Search and Filters */}
      <div className="flex flex-col gap-3 mb-6">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <Input
              type="text"
              placeholder="Search by client name or postcode..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="tenders-search-input"
              className="pl-10 bg-[#1E2530] border-white/20 text-white placeholder:text-gray-500 focus:border-[#F5A623]"
            />
          </div>
          
          {hasActiveFilters && (
            <Button 
              variant="ghost" 
              onClick={clearFilters}
              className="text-gray-400 hover:text-white"
            >
              Clear filters
            </Button>
          )}
        </div>

        {/* Filter Chips */}
        <div className="flex flex-wrap gap-2">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger 
              className="w-auto min-w-[140px] bg-[#1E2530] border-white/20 text-white"
              data-testid="status-filter-select"
            >
              <Filter className="w-4 h-4 mr-2 text-gray-500" />
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent className="bg-[#2A3240] border-white/10">
              {STATUS_OPTIONS.map(status => (
                <SelectItem 
                  key={status} 
                  value={status}
                  className="text-white focus:bg-[#F5A623]/20"
                >
                  {status === 'Pipeline' ? 'Pipeline (Active + Submitted)' : status}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={projectTypeFilter} onValueChange={setProjectTypeFilter}>
            <SelectTrigger 
              className="w-[170px] bg-[#1E2530] border-white/20 text-white"
              data-testid="project-type-filter"
            >
              <Building2 className="w-4 h-4 mr-2 text-gray-500" />
              <SelectValue placeholder="Project Type" />
            </SelectTrigger>
            <SelectContent className="bg-[#2A3240] border-white/10">
              {PROJECT_TYPE_OPTIONS.map(type => (
                <SelectItem 
                  key={type} 
                  value={type}
                  className="text-white focus:bg-[#F5A623]/20"
                >
                  {type}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={planningStatusFilter} onValueChange={setPlanningStatusFilter}>
            <SelectTrigger 
              className="w-[180px] bg-[#1E2530] border-white/20 text-white"
              data-testid="planning-status-filter"
            >
              <SelectValue placeholder="Planning Status" />
            </SelectTrigger>
            <SelectContent className="bg-[#2A3240] border-white/10">
              {PLANNING_STATUS_OPTIONS.map(status => (
                <SelectItem 
                  key={status} 
                  value={status}
                  className="text-white focus:bg-[#F5A623]/20"
                >
                  {status}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={returnDateFilter} onValueChange={setReturnDateFilter}>
            <SelectTrigger 
              className="w-[180px] bg-[#1E2530] border-white/20 text-white"
              data-testid="return-date-filter"
            >
              <Clock className="w-4 h-4 mr-2 text-gray-500" />
              <SelectValue placeholder="Return Date" />
            </SelectTrigger>
            <SelectContent className="bg-[#2A3240] border-white/10">
              {RETURN_DATE_FILTER_OPTIONS.map(opt => (
                <SelectItem 
                  key={opt.value} 
                  value={opt.value}
                  className="text-white focus:bg-[#F5A623]/20"
                >
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger 
              className="w-[190px] bg-[#1E2530] border-white/20 text-white"
              data-testid="sort-by-select"
            >
              <SelectValue placeholder="Sort By" />
            </SelectTrigger>
            <SelectContent className="bg-[#2A3240] border-white/10">
              {SORT_OPTIONS.map(opt => (
                <SelectItem 
                  key={opt.value} 
                  value={opt.value}
                  className="text-white focus:bg-[#F5A623]/20"
                >
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Tenders List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-[#F5A623]" />
        </div>
      ) : tenders.length === 0 ? (
        <Card className="bg-[#2A3240] border-white/10">
          <CardContent className="py-12 text-center">
            <FileText className="w-12 h-12 mx-auto mb-4 text-gray-600" />
            <h3 className="text-lg font-medium text-white mb-2">No tenders found</h3>
            <p className="text-gray-400 mb-4">
              {hasActiveFilters ? 'Try adjusting your filters' : 'Get started by creating your first tender'}
            </p>
            {!hasActiveFilters && (
              <Button
                onClick={() => navigate('/tenders/new')}
                data-testid="empty-new-tender-btn"
                className="bg-[#F5A623] text-[#1E2530] hover:bg-[#D98E1D]"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Tender
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <Card className="bg-[#2A3240] border-white/10 overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="border-white/10 hover:bg-transparent">
                <TableHead className="text-gray-400 font-medium">Tender</TableHead>
                <TableHead className="text-gray-400 font-medium">Client</TableHead>
                <TableHead className="text-gray-400 font-medium hidden md:table-cell">Type</TableHead>
                <TableHead className="text-gray-400 font-medium">Status</TableHead>
                <TableHead className="text-gray-400 font-medium hidden lg:table-cell">Estimator</TableHead>
                <TableHead className="text-gray-400 font-medium hidden lg:table-cell">Value</TableHead>
                <TableHead className="text-gray-400 font-medium hidden lg:table-cell">Received</TableHead>
                <TableHead className="text-gray-400 font-medium hidden lg:table-cell">Return Date</TableHead>
                <TableHead className="text-gray-400 font-medium hidden lg:table-cell">Submitted</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {tenders.map((tender, index) => (
                <TableRow 
                  key={tender.id}
                  onClick={() => navigate(`/tenders/${tender.id}`)}
                  className="border-white/5 hover:bg-white/5 cursor-pointer transition-colors"
                  data-testid={`tender-row-${index}`}
                >
                  <TableCell className="py-4">
                    <div className="flex items-center gap-2">
                      <span 
                        className="text-lg font-bold text-[#F5A623] hover:text-[#F5A623]/80 transition-colors"
                        style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
                      >
                        T{tender.tender_number}
                      </span>
                      <Badge variant="outline" className="border-white/20 text-gray-400 text-xs">
                        {tender.tender_type || 'DEV'}
                      </Badge>
                    </div>
                    <p className="text-xs text-gray-500 truncate max-w-[150px] md:hidden">
                      {tender.client_name}
                    </p>
                  </TableCell>
                  <TableCell>
                    <p className="text-white font-medium truncate max-w-[180px]" title={tender.client_name}>
                      {tender.client_name}
                    </p>
                    <p className="text-xs text-gray-500 truncate max-w-[180px]">
                      {tender.address}, {tender.postcode}
                    </p>
                  </TableCell>
                  <TableCell className="hidden md:table-cell">
                    <div>
                      <span className="text-white text-sm font-medium">
                        {tender.primary_project_type || (Array.isArray(tender.project_types) ? tender.project_types[0] : tender.project_types) || 'Other'}
                      </span>
                      {Array.isArray(tender.project_types) && tender.project_types.length > 1 && (
                        <p className="text-xs text-gray-500 mt-0.5">
                          +{tender.project_types.length - 1} more
                        </p>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1.5">
                      {canEditRecords ? (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button type="button" onClick={(e) => e.stopPropagation()} className="inline-flex items-center gap-1 rounded-full outline-none focus-visible:ring-2 focus-visible:ring-[#F5A623]/60" title="Change status" data-testid={`tender-status-trigger-${tender.id}`}>
                              <Badge className={`${STATUS_COLORS[tender.status] || STATUS_COLORS['Active']} text-xs cursor-pointer hover:brightness-125`}>
                                {tender.status}
                                <ChevronDown className="w-3 h-3 ml-1 opacity-70" />
                              </Badge>
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="start" className="bg-[#1E2530] border-white/10 text-white min-w-[160px]" onClick={(e) => e.stopPropagation()} data-testid={`tender-status-menu-${tender.id}`}>
                            <DropdownMenuLabel className="text-[11px] text-gray-500 font-normal">Change status to</DropdownMenuLabel>
                            {QUICK_STATUSES.filter((s) => s !== tender.status).map((s) => (
                              <DropdownMenuItem key={s} onSelect={() => setTimeout(() => setQuickStatus({ tender, nextStatus: s }), 0)} className="focus:bg-white/10 focus:text-white cursor-pointer" data-testid={`tender-status-option-${tender.id}-${s}`}>
                                <span className={`w-2 h-2 rounded-full mr-2 ${STATUS_DOT[s]}`} />{s}
                              </DropdownMenuItem>
                            ))}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      ) : (
                        <Badge className={`${STATUS_COLORS[tender.status] || STATUS_COLORS['Active']} text-xs`}>
                          {tender.status}
                        </Badge>
                      )}
                      {isOverdue(tender) && (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger>
                              <AlertTriangle className="w-4 h-4 text-orange-400" />
                            </TooltipTrigger>
                            <TooltipContent className="bg-[#1E2530] border-white/10 text-white max-w-[200px]">
                              <p>No submission date recorded — review required</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="hidden lg:table-cell">
                    <span className="text-gray-300 text-sm">
                      {tender.estimator_name || '-'}
                    </span>
                  </TableCell>
                  <TableCell className="hidden lg:table-cell">
                    {(tender.estimated_labour || tender.estimated_materials || tender.estimated_plant || tender.estimated_prosums || tender.estimated_prelims || tender.estimated_op) ? (
                      <span className="text-[#F5A623] font-medium">
                        {formatCurrency(
                          (tender.estimated_labour || 0) + 
                          (tender.estimated_materials || 0) + 
                          (tender.estimated_plant || 0) + 
                          (tender.estimated_prosums || 0) + 
                          (tender.estimated_prelims || 0) + 
                          (tender.estimated_op || 0)
                        )}
                      </span>
                    ) : (
                      <span className="text-gray-500">-</span>
                    )}
                  </TableCell>
                  <TableCell className="hidden lg:table-cell">
                    <div className="flex items-center text-xs text-gray-500">
                      <Calendar className="w-3 h-3 mr-1" />
                      {tender.date_received ? format(parseISO(tender.date_received), 'dd MMM yyyy') : '-'}
                    </div>
                  </TableCell>
                  <TableCell className="hidden lg:table-cell">
                    {tender.return_date ? (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger>
                            <div className={`flex items-center gap-1.5 text-xs px-2 py-1 rounded ${getReturnDateStatus(tender)?.color || 'text-gray-500'}`}>
                              <Clock className="w-3 h-3" />
                              <span>{format(parseISO(tender.return_date), 'dd MMM')}</span>
                              {getReturnDateStatus(tender) && (
                                <span className="font-medium">({getReturnDateStatus(tender).label})</span>
                              )}
                            </div>
                          </TooltipTrigger>
                          <TooltipContent className="bg-[#1E2530] border-white/10 text-white">
                            <p>Return deadline: {format(parseISO(tender.return_date), 'dd MMM yyyy')}</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    ) : (
                      <span className="text-gray-500">-</span>
                    )}
                  </TableCell>
                  <TableCell className="hidden lg:table-cell">
                    {tender.submitted_date ? (
                      <div className="flex items-center text-xs text-purple-400">
                        <Calendar className="w-3 h-3 mr-1" />
                        {format(parseISO(tender.submitted_date), 'dd MMM yyyy')}
                      </div>
                    ) : (
                      <span className="text-gray-500">-</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
      {quickStatus && (
        <QuickStatusDialog
          tender={quickStatus.tender}
          nextStatus={quickStatus.nextStatus}
          onClose={() => setQuickStatus(null)}
          onDone={(t) => {
            fetchTenders(); fetchDashboard();
            const visible = statusFilter === 'All' || statusFilter === t.status || (statusFilter === 'Pipeline' && ['Active', 'Submitted'].includes(t.status));
            if (!visible) toast.info(`T${t.tender_number} has moved out of this view — switch the status filter to “${t.status}” to see it`);
          }}
        />
      )}
    </div>
  );
}
